import { useEffect, RefObject } from "react";
import { useDispatch } from "react-redux";
import {
  setActiveElement,
  setCurrentPage,
} from "../redux/slices/elementsNavigationHelperReducer";

interface UseFieldNavigationProps {
  allCoordinates: any[];
  elementsNavigationData: any;
  signatureIndicatorRef: RefObject<any>;
  visitedFieldsRef: RefObject<Set<number>>;
  viewportHeight: number;
  pageIndex: number;
}

export const useFieldNavigation = ({
  allCoordinates,
  elementsNavigationData,
  signatureIndicatorRef,
  visitedFieldsRef,
  viewportHeight,
  pageIndex,
}: UseFieldNavigationProps) => {
  const dispatch = useDispatch();

  const handleStartAndScrollElement = (field?: any) => {
    try {
      console.log('handleStartAndScrollElement called with field: ', field);
      const targetField = field || elementsNavigationData;
      const { activeElementCoordinateId } = targetField;

      let nextIndex: number;
      if (field) {
        nextIndex = allCoordinates.findIndex(
          (item: any) => item.coordinateId === activeElementCoordinateId
        );
      } else {
        nextIndex =
          allCoordinates.findIndex(
            (item: any) => item.coordinateId === activeElementCoordinateId
          ) + 1;
      }
  
      const indexNo =
        activeElementCoordinateId === 0 || nextIndex - 1 === allCoordinates.length
          ? 0
          : nextIndex;

      const currentElementData: any = allCoordinates[indexNo];
      if (!currentElementData) return;

      // Scroll the PDF container to the target field
      const container = document.getElementById("pdfViewerContainer");
      if (container) {
        const headerHeight =
          document.querySelector(".menubar-container")?.getBoundingClientRect()
            .height || 0;
        const isMobile = window.innerWidth < 640;
        const scale = isMobile
          ? Math.min(1, Math.max(0.4, (window.innerWidth - 24) / 595.304))
          : 1;
        const pageHeight = isMobile ? Math.round(841.889 * scale) : (viewportHeight || 841.889);
        const pageGap = isMobile ? 16 : 20;
        const fieldY = isMobile ? currentElementData.y * scale : currentElementData.y;
        const scrollPadding = isMobile ? headerHeight + 20 : headerHeight + 40;

        const scrollY =
          currentElementData.pageNo * (pageHeight + pageGap) +
          fieldY -
          scrollPadding;
        container.scrollTo({ top: Math.max(0, scrollY), behavior: "smooth" });
      }

      visitedFieldsRef.current?.add(currentElementData.coordinateId);

      dispatch(
        setActiveElement({
          coordinateId: currentElementData.coordinateId,
          y: currentElementData.y,
          x: currentElementData.x,
          screenX: currentElementData.screen_x,
          screenY: currentElementData.screen_y,
          pageNumber: currentElementData.pageNo,
          height: currentElementData.height,
          width: currentElementData.width,
          isRequired: currentElementData.isRequired,
        })
      );
      dispatch(setCurrentPage({ pageIndex: currentElementData.pageNo }));
    } catch (err) {
      // Navigation error — silently ignore to avoid breaking signing flow
    }
  };

  // Update signature indicator position whenever the active element changes
  useEffect(() => {
    const { y, x, pageNumber } = elementsNavigationData;

    if (signatureIndicatorRef.current && y > 0 && x > -1) {
      const currentPageElements = allCoordinates.find(
        (item: any) => item.pageNo === pageNumber
      );

      if (currentPageElements) {
        const isMobile = window.innerWidth < 640;
        const scale = isMobile
          ? Math.min(1, Math.max(0.4, (window.innerWidth - 24) / 595.304))
          : 1;
        const pageHeight = isMobile ? Math.round(841.889 * scale) : (viewportHeight || 841.889);
        const pageGap = isMobile ? 16 : 20;
        const fieldY = isMobile ? y * scale : y;
        const fieldX = isMobile ? x * scale : x;

        const absoluteTop =
          currentPageElements.pageNo > 0
            ? currentPageElements.pageNo * (pageHeight + pageGap) + fieldY
            : fieldY;

        const isNearTop = fieldY < 80;

        signatureIndicatorRef.current.style.left = `${fieldX}px`;

        if (isNearTop) {
          signatureIndicatorRef.current.style.top = `${absoluteTop + 35}px`;
          signatureIndicatorRef.current.style.left = `${Math.max(0, fieldX - 110)}px`;
        } else {
          signatureIndicatorRef.current.style.top = `${absoluteTop - 25}px`;
          signatureIndicatorRef.current.style.left = `${fieldX}px`;
        }
      }
    }
 
    return () => {};
  }, [elementsNavigationData.pageNumber, elementsNavigationData.x, elementsNavigationData.y]);

  return { handleStartAndScrollElement };
};
